import * as Mutations from "./mutation-types"
import * as Actions from "./action-types"
import * as api from "@/api/api"
import { Tabs, MeasureSumMode } from "@/constants/appConstants"
import * as Helpers from "@/helpers"
import _ from "lodash";

const defaultAdsQuery = {
    type: "AdQueryModel",
    startDate: "",
    endDate: "",
    providerKeys: [],
    buyingAgencies: null,
    advertisers: null,
    products: null,
    filmCode: [],
    spot_types: [],
    productClasses: [],
    target_group: [],
}

const defaultAdsCampaignReportResults = {
    status: "NOT_LOADED",
    data: {
        PROVIDER: {
            status: "NOT_LOADED",
        },
        FILMCODE: {
            status: "NOT_LOADED",
        },
        DAY: {
            status: "NOT_LOADED",
        },
        WEEK: {
            status: "NOT_LOADED",
        },
        RoF: {
            status: "NOT LOADED",
        },
    },
}
const defaultAdsRoFQuery = {
    measureType: "absolute",
    aggregationType: "cumulative",
    maxFreq: 10,
    targetGroup: null,
}
const state = {
    queryCost: {
        data: null,
        isLoading: false,
        error: null,
    },
    filterByRating: false,
    adsInitStatus: "NOT_LOADED",
    adsQuery: _.cloneDeep(defaultAdsQuery),
    adsQueryDefault: _.cloneDeep(defaultAdsQuery),
    adsHasSetDefaultQuery: false,
    adsRoFQuery: {
        ...defaultAdsRoFQuery,
    },
    adsQueryForAPI: {},
    adsResults: {
        status: "NOT_LOADED",
    },
    adsRoFResults: {
        status: "NOT_LOADED",
    },
    adsCampaignMeasures: [],
    adsCampaignReportQuery: {},
    adsCampaignReportResults: {
        ...defaultAdsCampaignReportResults,
    },
    adsCampaignReportMeasureSumMode: {
        key: "PROVIDER",
        label: "Channel",
    },
    adsCampaignReportMeasureTypeSelected: "absolute_reach",
    adsCampaignReportCurrentTargetGroup: null,
    adsExportQuery: {
        include_detail_rows: true,
        include_header: true,
    },
    adsFormFields: [],
    adsColumnTypes: [],
    adsDefaultColumns: [],
    adsAllWithStarSelected: true,
    adsProviders: {},
    adsHaveSelectedRow: false,
    adsUniverseSize: [],
    adsPanelSize: [],
    adsQueryBeforeChange: {},
    adsHasExcludedRow: false,
    adsStringForFilterBeforeChange: "",
    adsSumVisible: false,
}

const convertFromQueryToQueryForAPI = q => {
    // transform internal query object to one that fits our API
    const defaultAdsQueryForAPI = {
        type: "AdQueryModel",
        productClasses: q.productClasses,
        startDate: q.startDate,
        endDate: q.endDate,
        filmCodes: q.filmCode,
        filterByRating: q.filterByRating,
        spotTypes: q.spot_types ?? [],
    }

    const APIQuery = {
        ...defaultAdsQueryForAPI,
        startDate: q.startDate ?? null,
        endDate: q.endDate ?? null,
        buyingAgency: q.buyingAgencies?.id ?? null,
        advertiser: q.advertisers?.id ?? null,
        product: q.products?.id ?? null,
        providerKeys: q.providerKeys,
        measures: q.target_group ? q.target_group : [],
    }
    return APIQuery
}

const executeQuery = async (context, query) => {
    // update queryForAPI to make debugging easier
    context.commit(Mutations.setAdsQueryForAPI, query)

    try {
        var r = await api.executeAdsQuery(query)

        // got a response
        // check if we got any rows
        if (r.ads.length == 0) {
            context.commit(Mutations.setAdsResults, {
                status: "EMPTY_RESULT",
            })
        } else {
            context.commit(Mutations.setAdsUniverseSize, r.reportData.universeSize)            
            context.commit(Mutations.setAdsPanelSize, r.reportData.panelSize)
            let query = context.getters.getAdsQuery
            let levelOneAdsWithLevelTwoAds = r.ads
                .filter(a => a.isGroupTotal && !a.isTotal)
                .map((a, i) => {
                    for (let i = 0; i < a.measures.length; i++) {
                        if (a.measures[i].trim().length == 0) {
                            a[`measure_${i + 1}`] = "0"
                        } else {
                            a[`measure_${i + 1}`] = a.measures[i]
                        }
                    }
                    let levelTwoAds = r.ads
                        .filter(lv2a => lv2a.filmCodeKey === a.filmCodeKey && lv2a.date === a.date && !lv2a.isGroupTotal)
                        .map(a => {
                            for (let i = 0; i < a.measures.length; i++) {
                                if (a.measures[i].trim().length == 0) {
                                    a[`measure_${i + 1}`] = "0"
                                } else {
                                    a[`measure_${i + 1}`] = a.measures[i]
                                }
                            }
                            return {
                                ...a,
                                chosen: false,
                            }
                        })
                    Helpers.setLevelOneValuesForAds(a, levelTwoAds, query.columns)
                    return {
                        ...a,
                        levelTwoAds: levelTwoAds,
                        index: i,
                        chosen: false,
                    }
                })
            let fields = context.getters.getAdsColumnTypes
                .filter(c => query.columns.includes(c.value))
                .map(c => {
                    return {
                        key: c.value,
                        label: c.text,
                        sortable: true,
                        isMeasure: false,
                    }
                })
            for (let i = 0; i < query.target_group.length; i++) {
                fields.push({
                    key: `measure_${i + 1}`,
                    label: `${query.target_group[i].selectedMeasureText.header} ${query.target_group[i].selectedMeasureText.subHeader}`,
                    header: query.target_group[i].selectedMeasureText.header,
                    subHeader: query.target_group[i].selectedMeasureText.subHeader,
                    sortable: true,
                    tdClass: "measureTdClass",
                    thClass: "measurethclass",
                    isMeasure: true,
                })
            }
            context.commit(Mutations.setAdsResults, {
                status: "LOADED",
                sortBy: "measure_1",
                sortDesc: true,
                data: {
                    levelOneAdsWithLevelTwoAds: levelOneAdsWithLevelTwoAds,
                    columns: fields,
                    summary: r.summary,
                },
            })
        }
    } catch (error) {
        context.commit(Mutations.setAdsResults, {
            status: "FAILED",
            data: null,
            error: error,
        })
    }
}

const executeCampaignReportQuery = async (context, query, measureSumModeKey, initialize) => {
    // update queryForAPI to make debugging easier
    query.measureSumModeKey = measureSumModeKey
    if (initialize) {
        context.commit(Mutations.setAdsCampaignReportQuery, query)
    } else {
        context.commit(Mutations.setAdsCampaignReportQuery, {
            ...context.getters.getAdsCampaignReportQuery,
            measureSumModeKey: measureSumModeKey,
        })
    }

    try {
        var r = await api.executeAdsQuery(query)
        // Check if we received any rows
        if (r.ads.length == 0) {
            context.commit(Mutations.setAdsCampaignReportResults, {
                status: "EMPTY_RESULT",
            })
        } else {
            // Measure sum mode decides which non-measure columns we select
            switch (measureSumModeKey) {
                case MeasureSumMode.PROVIDER.key:
                    query.columns = [
                        {
                            key: "providerName",
                            label: "Channel name",
                            sortable: true,
                        },
                    ]
                    break
                case MeasureSumMode.FILMCODE.key:
                    query.columns = [
                        {
                            key: "filmcode",
                            label: "Film code",
                            sortable: true,
                        },
                    ]
                    break
                case MeasureSumMode.DAY.key:
                    query.columns = [
                        {
                            key: "date",
                            label: "Day",
                            sortable: true,
                        },
                    ]
                    break
                case MeasureSumMode.WEEK.key:
                    query.columns = [
                        {
                            key: "yearWeek",
                            label: "Week",
                            sortable: true,
                        },
                    ]
                    break
                default:
                    query.columns = [
                        {
                            key: "date",
                            label: "Day",
                            sortable: true,
                        },
                    ]
            }
            const measures = context.getters.getAdsCampaignReportQuery.measures
            let universeSize = r.reportData.universeSize.map(item => {
                const [key, value] = item.split(";").map(part => part.trim())
                return {
                    key: key,
                    value,
                }
            })
            let levelOneAdsWithLevelTwoAds = r.ads
                .filter(a => !a.isTotal)
                .map((a, i) => {
                    for (let i = 0; i < a.measures.length; i++) {
                        a[`measure_${i + 1}`] = a.measures[i]
                        const ageText = measures[i].selectedMeasureText.subHeader.replace(/\s.*/, "")
                        let measureWithoutSpaces = (a.measures[i] || "").trim().replace(/\s/g, "")
                        a[`measure_${i + 1}_rel`] = !measureWithoutSpaces
                            ? ""
                            : ((parseFloat(a.measures[i].replace(",", ".")) / parseFloat(universeSize.find(u => u.key === ageText).value)) * 1000 * 100).toFixed(1).replace(".", ",")
                        if (a.measures[i].trim().length == 0) {
                            a[`measure_${i + 1}`] = "0"
                        }
                    }
                    if (measureSumModeKey === MeasureSumMode.PROVIDER.key && a.providerGroup !== undefined && a.providerGroup == "Sites") {
                        a.providerNameVhtml = `<span style="color: #337ab7; font-weight: bold;">${a.providerName}</span>`
                    }
                    return {
                        ...a,
                        index: i,
                    }
                })
            // Add missing year to most recent week
            if (measureSumModeKey === MeasureSumMode.WEEK.key) {
                try {
                    levelOneAdsWithLevelTwoAds = Helpers.updateYearWeek(levelOneAdsWithLevelTwoAds)
                } catch (err) {
                    throw new Error("Error updating year and week column in ad data.")
                }
            }
            let measureFields = []
            for (let i = 0; i < measures.length; i++) {
                measureFields.push({
                    key: `measure_${i + 1}`,
                    label: `${measures[i].selectedMeasureText.header} ${measures[i].selectedMeasureText.subHeader}`,
                    header: measures[i].selectedMeasureText.header,
                    subHeader: measures[i].selectedMeasureText.subHeader,
                    sortable: true,
                    tdClass: "measureTdClass",
                    thClass: "measurethclass",
                })
            }
            let totalRow = r.ads.find(x => x.isTotal)
            for (let i = 0; i < totalRow.measures.length; i++) {
                totalRow[`measure_${i + 1}`] = totalRow.measures[i]
                const ageText = measures[i].selectedMeasureText.subHeader.replace(/\s.*/, "")
                let measureWithoutSpaces = (totalRow.measures[i] || "").trim().replace(/\s/g, "")
                totalRow[`measure_${i + 1}_rel`] = !measureWithoutSpaces
                    ? ""
                    : ((parseFloat(totalRow.measures[i].replace(",", ".")) / parseFloat(universeSize.find(u => u.key === ageText).value)) * 1000 * 100).toFixed(1).replace(".", ",")
            }
            let data = JSON.parse(JSON.stringify(context.getters.getAdsCampaignReportResults.data))
            let targetGroupMeasures = measureFields.filter(m => m.subHeader.includes(context.getters.getAdsCampaignReportCurrentTargetGroup))
            let lastColumnKey = targetGroupMeasures[targetGroupMeasures.length - 1].key
            data[measureSumModeKey] = {
                viewTypeColumns: query.columns,
                status: "LOADED",
                levelOneAdsWithLevelTwoAds: Helpers.sortItems(levelOneAdsWithLevelTwoAds, lastColumnKey, true),
                totalRow: totalRow,
            }

            if (initialize) {
                data.measureColumns = measureFields
            }

            const payload = {
                ...context.getters.getAdsCampaignReportResults,
                status: "LOADED",
                data: data,
            }

            await context.commit(Mutations.setAdsCampaignReportResults, payload)
        }
    } catch (error) {
        console.error(error)
        context.commit(Mutations.setAdsCampaignReportResults, {
            status: "FAILED",
            data: defaultAdsCampaignReportResults.data,
            error: error,
        })
    }
}

const actions = {
    [Actions.getAdsProductClasses]: async () => {
        const productClasses = await api.getProductClasses();
        return productClasses;
      },
    [Actions.getAdsProviders]: async context => {
        const providerObjects = await api.getProviders(Tabs.AdTab)
        const providers = Object.keys(providerObjects).map(k => providerObjects[k])
        const sites = providers.filter(x => x.provider_type == "SITE" && x.exists_ad)
        const channels = providers
            .filter(x => x.provider_type == "CHANNEL" && x.exists_ad)
            .reduce((p, c) => {
                return {
                    ...p,
                    [c.provider_group]: [...(p[c.provider_group] ?? []), c],
                }
            }, {})
        await context.commit(Mutations.setAdsProviders, {
            sites: sites,
            channels: channels,
        })
    },
    [Actions.getAdsColumnTypes]: async () => {
        return await api.getColumnTypes("ADS")
    },
    [Actions.getAdsMeasures]: async () => {
        return await api.getMeasures("ADS")
    },
    [Actions.getAdsBuyingAgencies]: async () => {
        let dateQuery = {
            startDate: state.adsQuery.startDate,
            endDate: state.adsQuery.endDate,
        }
        return await api.getBuyingAgencies(dateQuery)
    },
    [Actions.getAdsAdvertisers]: async () => {
        let dateQuery = {
            startDate: state.adsQuery.startDate,
            endDate: state.adsQuery.endDate,
        }

        return await api.getAdvertisers(dateQuery)
    },
    [Actions.getAdsProducts]: async () => {
        let dateQuery = {
            startDate: state.adsQuery.startDate,
            endDate: state.adsQuery.endDate,
        }

        return await api.getProducts(dateQuery)
    },
    // this initializes the table and query, so use when pressing 'Search'
    [Actions.executeAdsQuery]: async context => {
        context.commit(Mutations.setAdsResults, {
            status: "LOADING",
            data: null,
        })
        // prepare query
        const query = convertFromQueryToQueryForAPI(context.state.adsQuery)

        // check if the query is OK to run and return to the interface, otherwise set queryCost so we handle it from there
        context.commit(Mutations.setQueryCostLoading, true)
        context.commit(Mutations.setQueryCostError, null)

        try {
            const queryCostData = await api.getQueryCostAds(query)
            await context.commit(Mutations.setQueryCostData, queryCostData)
        } catch (error) {
            await context.commit(Mutations.setQueryCostError, error)
        } finally {
            await context.commit(Mutations.setQueryCostLoading, false)
        }

        if (context.state.queryCost.error == null && context.state.queryCost.data.queryClass == "Normal") {
            await context.commit(Mutations.setAdsQueryForAPI, query)
            await executeQuery(context, query)
        } else {
            context.commit(Mutations.setAdsResults, {
                status: "NOT_LOADING",
                data: null,
            })
        }
    },
    [Actions.executeAdsRoFQuery]: async context => {
        try {
            context.commit(Mutations.setAdsRoFResults, {
                status: "LOADING",
                data: null,
            })
            let rowkeys = context.getters.getAdsResults.data.levelOneAdsWithLevelTwoAds
                .filter(lvlOneAd => lvlOneAd.chosen)
                .map(c => {
                    return {
                        date: c.date,
                        filmcodekey: c.filmCodeKey,
                        providerkeys: c.providerKeys,
                    }
                })
            let rofQuery = {
                ...context.getters.getAdsRoFQuery,
                startDate: context.getters.getAdsQueryForAPI.startDate,
                endDate: context.getters.getAdsQuery.endDate,
                rowKeys: [...rowkeys],
            }
            var r = await api.executeAdsRoFQuery(rofQuery)
            // save results formatted according to query
            const formatFunc = function(val) {
                return context.getters.getAdsRoFQuery.measureType == "absolute" ? Math.round(val / 1000, 0).toLocaleString("sv-Sv") : `${Math.round(val * 1000) / 10}`
            }

            context.commit(Mutations.setAdsRoFResults, {
                status: "LOADED",
                data: {
                    ...r,
                    frequencyMatrix: r.frequencyMatrix.map(row => row.map(cell => formatFunc(cell))),
                    reach1PlusAcc: r.reach1PlusAcc.map(cell => formatFunc(cell)),
                    averageFrequency: r.averageFrequency,
                    rating: r.rating.map(cell => formatFunc(cell)),
                },
                tableData: {
                    initialTableColumns: [],
                    initialTableItem: [],
                    roFColumns: [],
                    allRofRows: [],
                },
            })
        } catch (error) {
            context.commit(Mutations.setAdsRoFResults, {
                status: "FAILED",
                data: null,
                error: error,
            })
        }
    },
    [Actions.executeAdsCampaignReportQuery]: async (context, payload) => {
        const measureSumModeKey = payload.measureSumModeKey || MeasureSumMode.DAY.key
        const initialize = payload.initialize !== undefined ? payload.initialize : false

        if (initialize) {
            await context.commit(Mutations.setAdsCampaignReportMeasureSumMode, MeasureSumMode.DAY)
            await context.commit(Mutations.setAdsCampaignReportResults, {
                status: "LOADING",
                data: defaultAdsCampaignReportResults.data,
            })
            await context.commit(Mutations.setAdsCampaignReportQuery, {})
        }
        let query = {}
        if (initialize) {
            await context.commit(Mutations.setAdsCampaignReportQuery, {})
            let rowkeys = context.getters.getAdsResults.data.levelOneAdsWithLevelTwoAds
                .filter(lvlOneAd => lvlOneAd.chosen)
                .map(c => {
                    return {
                        date: c.date,
                        filmcodekey: c.filmCodeKey,
                        providerkeys: c.providerKeys,
                    }
                })

            const chosenAds = context.getters.getAdsResults.data.levelOneAdsWithLevelTwoAds.filter(ad => ad.chosen)

            // Initialize your measures and construct your query here
            let { measures, targetGroups } = Helpers.buildMeasureDataForCampaignReport(context.state.adsQueryForAPI.measures)
            context.commit(Mutations.setAdsCampaignReportCurrentTargetGroup, targetGroups[0].label)

            // Flatten the data to capture both top level and nested levelTwoAds properties
            const flattenedAds = chosenAds.reduce((acc, ad) => {
                acc.push(ad)
                if (ad.levelTwoAds && ad.levelTwoAds.length) {
                    acc.push(...ad.levelTwoAds)
                }
                return acc
            }, [])
            // A helper function to get unique properties from the list
            const getUnique = (ads, prop) => [...new Set(ads.map(ad => ad[prop]))]

            // Date handling
            const validDates = getUnique(flattenedAds, "date")
                .filter(date => date && !isNaN(new Date(date)))
                .map(date => new Date(date))
                .sort((a, b) => a.getTime() - b.getTime())
            // Display in advance to user?
            let headerData = {}
            headerData.providers = getUnique(flattenedAds, "providerName")
            headerData.filmCodes = getUnique(flattenedAds, "filmcode")
            headerData.targetGroups = targetGroups.map(tg => tg.label)
            headerData.products = getUnique(flattenedAds, "productName")
            headerData.advertisers = getUnique(flattenedAds, "advertiserName")
            headerData.buyingAgencies = getUnique(flattenedAds, "buyingAgencyName")
            query = {
                ...convertFromQueryToQueryForAPI(context.state.adsQuery),
                measures: measures,
                rowKeys: rowkeys,
                headerData: headerData,
                measureSumModeKey: measureSumModeKey,
                targetGroups: targetGroups,
                selectedDates: validDates,
            }
            // Decide on date output format
            let dateString
            if (query.startDate === query.endDate) {
                dateString = query.startDate
            } else {
                dateString = `${query.startDate} - ${query.endDate}`
            }
            headerData.dateRange = dateString || "-"
            context.commit(Mutations.setAdsCampaignReportQuery, query)
        } else {
            query = context.getters.getAdsCampaignReportQuery
        }
        await executeCampaignReportQuery(context, query, measureSumModeKey, initialize)
    },
    [Actions.executeAdsRoFExport]: async context => {
        try {
            let adsQuery = context.getters.getAdsQuery
            let rofTableData = {
                ...context.getters.getAdsRoFResults.tableData,
            }
            const rofTableDataCopy = JSON.parse(JSON.stringify(rofTableData))
            const initialTableColumns = rofTableDataCopy.initialTableColumns.map(obj => obj.label).join(";")
            rofTableDataCopy.initialTableItem[0].channelNames = [
                ...new Set(
                    context.getters.getAdsResults.data.levelOneAdsWithLevelTwoAds
                        .filter(p => p.chosen)
                        .map(p => p.levelTwoAds)
                        .flat()
                        .map(p => p.providerName)
                ),
            ]
                .sort()
                .join(", ")
            rofTableDataCopy.initialTableItem[0].filmCodeKeys = [...context.getters.getAdsResults.data.levelOneAdsWithLevelTwoAds.filter(a => a.chosen).map(a => a.filmCodeKey)].join(", ")
            const initialTableItem = rofTableDataCopy.initialTableItem
                .map(row => {
                    let csvInitialRowData = []
                    for (const column of rofTableDataCopy.initialTableColumns) {
                        csvInitialRowData.push(row[column.key])
                    }
                    return csvInitialRowData.join(";")
                })
                .join("\n")
            let csvContent = `${initialTableColumns}\n${initialTableItem}`
            if (rofTableDataCopy.roFColumns.length > 0) {
                csvContent += `\n\n${rofTableDataCopy.roFColumns.map(obj => obj.label).join(";")}`
                for (const row of rofTableDataCopy.allRofRows) {
                    let csvAllRofRowsData = []
                    for (const column of rofTableDataCopy.roFColumns) {
                        csvAllRofRowsData.push(row[column.key] ?? "")
                    }
                    csvContent += `\n${csvAllRofRowsData.join(";")}`
                }
            }
            let fileName = `${context.getters.getExcelExportFilePreName} CROSS-PLATFORM_AdRoF_${adsQuery.startDate}_${adsQuery.endDate}.csv`
            Helpers.downloadBlob(csvContent, fileName, "text/csv;charset=utf-8;")
        } catch (error) {
            console.error(error)
        }
    },
    [Actions.executeScheduleQuery]: async (context, options) => {
        // prepare query
        const apiQuery = convertFromQueryToQueryForAPI(context.state.adsQuery)

        await context.commit(Mutations.setAdsQueryForAPI, apiQuery)
        //await executeQuery(context, query)

        apiQuery.include_detail_rows = context.state.adsExportQuery.include_detail_rows
        apiQuery.include_header = context.state.adsExportQuery.include_header

        apiQuery.Columns = context.state.adsQuery.columns

        if (apiQuery.include_header) {
            apiQuery.headeritems = Helpers.createExportAdHeaders(context.getters, options.tab, options.messageExceededRecommendedPeriod, apiQuery)
        }

        return await api.createScheduledReportQuery(options.reportname, apiQuery)
    },
    [Actions.executeAdsExportQuery]: async (context, options) => {
        let status = context.getters.getAdsResults.status
        let apiQuery = {}
        if (status === "LOADED")
            apiQuery = {
                ...context.state.adsQueryForAPI,
            }
        else apiQuery = convertFromQueryToQueryForAPI(context.state.adsQuery)
        apiQuery.include_detail_rows = context.state.adsExportQuery.include_detail_rows
        apiQuery.include_header = context.state.adsExportQuery.include_header
        apiQuery.sortBy = context.state.adsResults.sortBy
        apiQuery.sortByDesc = context.state.adsResults.sortDesc
        apiQuery.Columns = context.state.adsQuery.columns

        if (apiQuery.include_header) {
            apiQuery.headeritems = Helpers.createExportAdHeaders(context.getters, options.tab, options.messageExceededRecommendedPeriod, apiQuery)
        }
        if (status === "LOADED") {
            apiQuery.rowKeys = context.getters.getAdsResults.data.levelOneAdsWithLevelTwoAds.map(c => {
                return {
                    date: c.date,
                    filmcodekey: c.filmCodeKey,
                    providerkeys: c.providerKeys,
                }
            })
        }
        return await api.executeExportAdsQuery(apiQuery)
    },
}

const getters = {
    getAdsQuery: state => state.adsQuery,
    getAdsQueryDefault: state => state.adsQueryDefault,
    getAdsHasSetDefaultQuery: state => state.adsHasSetDefaultQuery,
    getAdsRoFQuery: state => state.adsRoFQuery,
    getAdsResults: state => state.adsResults,
    getAdsRoFResults: state => state.adsRoFResults,
    getAdsCampaignReportQuery: state => state.adsCampaignReportQuery,
    getAdsCampaignReportResults: state => state.adsCampaignReportResults,
    getAdsCampaignReportMeasureSumMode: state => state.adsCampaignReportMeasureSumMode,
    getAdsCampaignReportMeasureTypeSelected: state => state.adsCampaignReportMeasureTypeSelected,
    getAdsCampaignReportCurrentTargetGroup: state => state.adsCampaignReportCurrentTargetGroup,
    getAdsQueryForAPI: state => state.adsQueryForAPI,
    getAdsColumnTypes: ({ adsColumnTypes }) => adsColumnTypes,
    getAdsInitStatus: ({ adsInitStatus }) => adsInitStatus,
    getAdsDefaultColumns: state => state.adsDefaultColumns,
    getAdsAllWithStarSelected: state => state.adsAllWithStarSelected,
    getAdsProviders: state => state.adsProviders,
    getAdsHaveSelectedRow: state => state.adsHaveSelectedRow,
    getAdsExportQuery: state => state.adsExportQuery,
    getAdsFormFields: state => state.adsFormFields,
    getAdsPanelSize: state => state.adsPanelSize,
    getAdsUniverseSize: state => state.adsUniverseSize,
    getAdsQueryCost: state => state.queryCost,
    getAdsQueryBeforeChange: state => state.adsQueryBeforeChange,
    getAdsStringForFilterBeforeChange: state => state.adsStringForFilterBeforeChange,
    getAdsHasExcludedRow: state => state.adsHasExcludedRow,
    getAdsSumVisible: state => state.adsSumVisible,
}

const mutations = {
    [Mutations.setAdsQueryProviders]: (state, payload) => (state.adsQuery.providerKeys = payload),
    [Mutations.setAdsQuery]: (state, payload) => (state.adsQuery = payload),
    [Mutations.setAdsRoFQuery]: (state, payload) => (state.adsRoFQuery = payload),
    [Mutations.setAdsQueryDefault]: (state, payload) => (state.adsQueryDefault = payload),
    [Mutations.setAdsHasSetDefaultQuery]: (state, payload) => (state.adsHasSetDefaultQuery = payload),
    [Mutations.setAdsResults]: (state, payload) => (state.adsResults = payload),
    [Mutations.setAdsRoFResults]: (state, payload) => (state.adsRoFResults = payload),
    [Mutations.setAdsRoFResultsTableData]: (state, payload) => (state.adsRoFResults.tableData = payload),
    [Mutations.setAdsCampaignReportQuery]: (state, payload) => (state.adsCampaignReportQuery = payload),
    [Mutations.setAdsCampaignReportResults]: (state, payload) => (state.adsCampaignReportResults = payload),
    [Mutations.setAdsCampaignReportMeasureSumMode]: (state, payload) => (state.adsCampaignReportMeasureSumMode = payload),
    [Mutations.setAdsCampaignReportCurrentTargetGroup]: (state, payload) => (state.adsCampaignReportCurrentTargetGroup = payload),
    [Mutations.setAdsCampaignReportMeasureTypeSelected]: (state, payload) => (state.adsCampaignReportMeasureTypeSelected = payload),
    [Mutations.setAdsColumnTypes]: (state, payload) => (state.adsColumnTypes = payload),
    [Mutations.setAdsInitStatus]: (state, payload) => (state.adsInitStatus = payload),
    [Mutations.setAdsQueryForAPI]: (state, payload) => (state.adsQueryForAPI = payload),
    [Mutations.setAdsChannels]: (state, payload) => (state.adsChannels = payload),
    [Mutations.setAdsStartdate]: (state, payload) => (state.adsQuery.startDate = payload),
    [Mutations.setAdsEnddate]: (state, payload) => (state.adsQuery.endDate = payload),
    [Mutations.setAdsDefaultColumns]: (state, payload) => (state.adsDefaultColumns = payload),
    [Mutations.setAdsAllWithStarSelected]: (state, payload) => (state.adsAllWithStarSelected = payload),
    [Mutations.setAdsProviders]: (state, payload) => (state.adsProviders = payload),
    [Mutations.setAdsHaveSelectedRow]: (state, payload) => (state.adsHaveSelectedRow = payload),
    [Mutations.setAdsExportQuery]: (state, payload) => (state.adsExportQuery = payload),
    [Mutations.setAdsFormFields]: (state, payload) => (state.adsFormFields = payload),
    [Mutations.setAdsUniverseSize]: (state, payload) => (state.adsUniverseSize = payload),
    [Mutations.setAdsPanelSize]: (state, payload) => (state.adsPanelSize = payload),
    [Mutations.setQueryCostData]: (state, payload) => (state.queryCost.data = payload),
    [Mutations.setQueryCostLoading]: (state, payload) => (state.queryCost.loading = payload),
    [Mutations.setQueryCostError]: (state, payload) => (state.queryCost.error = payload),
    [Mutations.setAdsQueryBeforeChange]: (state, payload) => (state.adsQueryBeforeChange = payload),
    [Mutations.setAdsStringForFilterBeforeChange]: (state, payload) => (state.adsStringForFilterBeforeChange = payload),
    [Mutations.setAdsHasExcludedRow]: (state, payload) => (state.adsHasExcludedRow = payload),
    [Mutations.setAdsSumVisible]: (state, payload) => (state.adsSumVisible = payload),
}

export default {
    state,
    getters,
    mutations,
    actions,
}
